import { apolloClient } from "@App/graphql/client";
import { GET_VEHICLETREE, type VehicletreeQuery } from "@App/graphql/queries";
import { actionCreators, type State, store } from "@App/state";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

/**
 * A custom hook that checks if all needed data was loaded from the api on component mount
 * and returns a method to parse the data to the url query parameters.
 */
export const useVehicletreeParameters = () => {
  const [searchParams] = useSearchParams();

  const dispatch = useDispatch();
  const {
    chooseBrand,
    changeBrandFilter,
    chooseGeneration,
    chooseMotorization,
    chooseBody,
    setMileage,
    chooseGearbox,
    addEquipment,
  } = bindActionCreators(actionCreators, dispatch);

  const brand = useSelector((state: State) => state.orders.brand);
  const generation = useSelector((state: State) => state.orders.generation);
  const motorization = useSelector((state: State) => state.orders.motorization);
  const body = useSelector((state: State) => state.orders.body);
  const gearbox = useSelector((state: State) => state.orders.gearbox);
  const mileage = useSelector((state: State) => state.orders.mileage);
  const equipments = useSelector((state: State) => state.orders.equipments);

  const brandQuery = searchParams.get("brand");
  const generationQuery = searchParams.get("generation");
  const motorizationQuery = searchParams.get("motorization");
  const bodyQuery = searchParams.get("body");
  const gearboxQuery = searchParams.get("gearbox");
  const mileageQuery = searchParams.get("mileage");
  const equipmentsQuery = searchParams.get("equipments");

  const fetchVehicletree = (
    brandId: number,
    generationId?: number,
    motorizationId?: number,
    bodyId?: number,
    gearboxId?: number,
  ) => {
    apolloClient
      .query<VehicletreeQuery>({
        query: GET_VEHICLETREE,
        variables: {
          ...(brandId && { brandId: +brandId }), // use + to convert string to number
          ...(generationId && { generationId: +generationId }), // use + to convert string to number
          ...(motorizationId && { motorizationId: +motorizationId }), // use + to convert string to number
          includeGenerations: !!brandId,
          includeMotorizations: !!generationId,
        },
      })
      .then((result) => {
        chooseBrand(result.data.brands[0]);
        changeBrandFilter(result.data.brands[0]);
        if (generationId) {
          chooseGeneration(result.data.brands[0]?.generations?.[0] ?? null);
        }
        if (motorizationId) {
          chooseMotorization(result.data.brands[0]?.generations?.[0]?.motorizations?.[0] ?? null);
        }
        if (bodyId && (!body || body.id != bodyId)) {
          if (result.data.brands[0]?.generations[0]?.motorizations[0]?.bodies?.length > 0) {
            chooseBody(
              result.data.brands[0].generations[0].motorizations[0].bodies.find(
                (body) => body.id == bodyId,
              ),
            );
          } else {
            chooseBody({
              id: 1,
              name: "Standard",
            });
          }
        }
        if (gearboxId && (!gearbox || gearbox.id != gearboxId)) {
          if (result.data.brands[0]?.generations[0]?.motorizations[0]?.gearboxes?.length > 0) {
            chooseGearbox(
              result.data.brands[0].generations[0].motorizations[0].gearboxes.find(
                (gearbox) => gearbox.id == gearboxId,
              ),
            );
          } else {
            chooseGearbox({
              id: 1,
              name: "ManualGearBox",
              formatted_name: "Schaltgetriebe",
            });
          }
        }
      });
  };

  useEffect(() => {
    if (mileageQuery && !mileage) {
      setMileage(parseInt(mileageQuery));
    }

    if (equipmentsQuery && !equipments) {
      equipmentsQuery
        .split(",")
        .forEach((equipment, index) => addEquipment({ id: index, name: equipment }));
    }

    if (
      (brandQuery && (!brand || brand.id !== parseInt(brandQuery))) ||
      (generationQuery && (!generation || generation.id !== parseInt(generationQuery))) ||
      (motorizationQuery && (!motorization || motorization.id !== parseInt(motorizationQuery)))
    ) {
      fetchVehicletree(
        parseInt(brandQuery),
        parseInt(generationQuery),
        parseInt(motorizationQuery),
        parseInt(bodyQuery),
        parseInt(gearboxQuery),
      );
    }
  }, []);

  const vehicletreeQueryParams = (): Record<string, string> => {
    return {
      // add brand if existing
      ...(store.getState().orders.brand && {
        brand: store.getState().orders.brand.id.toString(),
      }),
      // add generation if existing
      ...(store.getState().orders.generation && {
        generation: store.getState().orders.generation.id.toString(),
      }),
      // add motorization if existing
      ...(store.getState().orders.motorization && {
        motorization: store.getState().orders.motorization.id.toString(),
      }),
      // add body if existing
      ...(store.getState().orders.body && {
        body: store.getState().orders.body.id.toString(),
      }),
      // add gearbox if existing
      ...(store.getState().orders.gearbox && {
        gearbox: store.getState().orders.gearbox.id.toString(),
      }),
      // add mileage if existing
      ...(store.getState().orders.mileage && {
        mileage: store.getState().orders.mileage.toString(),
      }),
      // add equipments if existing
      ...(store.getState().orders.equipments &&
        store.getState().orders.equipments.length > 0 && {
          equipments: store
            .getState()
            .orders.equipments.map((equipment) => equipment.name)
            .join(","),
        }),
    };
  };

  return { vehicletreeQueryParams };
};
